import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Play, BookOpen, Trash2, FolderInput } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  not_started: { label: "Не начато", className: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" },
  learning: { label: "Изучается", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" },
  completed: { label: "Завершено", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300" },
};

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function VideoCard({ video, folders = [] }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const status = STATUS_LABELS[video.status] || STATUS_LABELS.not_started;

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/videos/${video.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      queryClient.invalidateQueries({ queryKey: ["folders"] });
      toast.success("Видео удалено из библиотеки");
    },
    onError: () => toast.error("Не удалось удалить видео"),
  });

  const folderMutation = useMutation({
    mutationFn: (folderId) => api.patch(`/videos/${video.id}/folder`, { folder_id: folderId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      queryClient.invalidateQueries({ queryKey: ["folders"] });
      toast.success("Видео перемещено");
    },
    onError: () => toast.error("Не удалось переместить видео"),
  });

  return (
    <div
      onClick={() => navigate(`/player/${video.id}`)}
      data-testid={`video-card-${video.id}`}
      className="group cursor-pointer rounded-xl border border-border bg-card overflow-hidden hover:-translate-y-1 hover:border-primary/50 transition-all duration-200"
    >
      <div className="relative aspect-video bg-muted overflow-hidden">
        <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
          <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="white" />
        </div>
        <span className="absolute bottom-2 right-2 text-xs bg-black/70 text-white px-1.5 py-0.5 rounded">
          {formatDuration(video.duration)}
        </span>
        <Badge className={`absolute top-2 left-2 border-0 ${status.className}`}>{status.label}</Badge>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              data-testid={`folder-assign-button-${video.id}`}
              className="absolute top-2 right-9 w-7 h-7 rounded-lg bg-black/60 hover:bg-primary text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <FolderInput className="w-3.5 h-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent onClick={(e) => e.stopPropagation()} data-testid={`folder-assign-menu-${video.id}`}>
            <DropdownMenuItem onClick={() => folderMutation.mutate(null)} data-testid={`folder-assign-none-${video.id}`}>
              Без папки
            </DropdownMenuItem>
            {folders.map((f) => (
              <DropdownMenuItem key={f.id} onClick={() => folderMutation.mutate(f.id)} data-testid={`folder-assign-${f.id}-${video.id}`}>
                {f.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              data-testid={`delete-video-button-${video.id}`}
              className="absolute top-2 right-2 w-7 h-7 rounded-lg bg-black/60 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent onClick={(e) => e.stopPropagation()} data-testid={`delete-video-dialog-${video.id}`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Удалить видео?</AlertDialogTitle>
              <AlertDialogDescription>
                "{video.title}" будет удалено из библиотеки. Изученные слова из словаря удалены не будут.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`delete-video-cancel-${video.id}`}>Отмена</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate()}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid={`delete-video-confirm-${video.id}`}
              >
                Удалить
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <div className="p-3 space-y-2">
        <h3 className="font-heading font-semibold text-sm leading-snug line-clamp-2" title={video.title}>
          {video.title}
        </h3>
        <p className="text-xs text-muted-foreground truncate">{video.author}</p>
        <Progress value={video.progress_percent || 0} className="h-1.5" />
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-0.5">
          <span className="flex items-center gap-1">
            <BookOpen className="w-3 h-3" /> {video.words_learned_count || 0} слов
          </span>
          <span>{Math.round(video.progress_percent || 0)}%</span>
        </div>
      </div>
    </div>
  );
}
